import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Building2, TrendingUp, Users, Home, Award } from 'lucide-react';

export const AgentSection = ({ t }) => {
  const specialties = [
    { icon: Building2, label: t.agent.luxury },
    { icon: TrendingUp, label: t.agent.investment },
    { icon: Users, label: t.agent.international },
    { icon: Home, label: t.agent.airbnb }
  ];

  return (
    <section id="agent" className="py-32 bg-gradient-to-b from-black via-slate-950 to-black relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#C5A05E]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#C5A05E]/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20 space-y-6">
          <div className="inline-block px-6 py-2 bg-[#C5A05E]/10 backdrop-blur-sm border border-[#C5A05E]/30 rounded-full text-[#C5A05E] text-sm font-semibold uppercase tracking-wider mb-4 animate-fadeInUp">
            Expert
          </div>
          <h2 className="text-5xl md:text-7xl font-bold text-white animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            {t.agent.title}
          </h2>
          <div className="flex items-center justify-center gap-3 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="h-px w-24 bg-gradient-to-r from-transparent to-[#C5A05E]" />
            <div className="w-2 h-2 bg-[#C5A05E] rounded-full" />
            <div className="h-px w-24 bg-gradient-to-l from-transparent to-[#C5A05E]" />
          </div>
        </div>

        {/* Agent Card */}
        <div className="max-w-6xl mx-auto animate-fadeInUp" style={{ animationDelay: '0.3s', opacity: 0, animationFillMode: 'forwards' }}>
          <div className="relative">
            {/* Glowing effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-[#C5A05E] via-[#D4B06E] to-[#C5A05E] opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-500 rounded-3xl" />
            
            <Card className="relative backdrop-blur-2xl bg-white/5 border border-white/10 overflow-hidden">
              <div className="grid md:grid-cols-5 gap-0">
                {/* Agent Image */}
                <div className="md:col-span-2 relative group">
                  <div className="aspect-square md:aspect-auto md:h-full overflow-hidden relative">
                    <img
                      src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&q=80"
                      alt={t.agent.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {/* Dark gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                    
                    {/* Badge on image */}
                    <div className="absolute bottom-8 left-8 right-8">
                      <div className="backdrop-blur-2xl bg-black/60 border border-[#C5A05E]/30 rounded-2xl p-6 shadow-2xl">
                        <div className="flex items-center gap-4 mb-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] flex items-center justify-center">
                            <Award className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="text-xl font-bold text-white">{t.agent.name}</div>
                            <div className="text-sm text-[#C5A05E] font-semibold">{t.agent.experience}</div>
                          </div>
                        </div>
                        {/* Mini stats */}
                        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-white">20+</div>
                            <div className="text-xs text-gray-400">Years</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-white">500+</div>
                            <div className="text-xs text-gray-400">Sales</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-white">98%</div>
                            <div className="text-xs text-gray-400">Success</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Agent Info */}
                <CardContent className="md:col-span-3 p-8 md:p-12 space-y-8">
                  {/* Bio */}
                  <p className="text-gray-300 leading-relaxed text-lg">
                    {t.agent.bio}
                  </p>

                  {/* Specialties */}
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                      <div className="w-1 h-8 bg-gradient-to-b from-[#C5A05E] to-transparent rounded-full" />
                      {t.agent.specialties}
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {specialties.map((specialty, index) => (
                        <div
                          key={index}
                          className="group/specialty relative flex items-center gap-4 p-5 backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 hover:border-[#C5A05E]/50 transition-all duration-300 hover:transform hover:scale-105 cursor-pointer"
                        >
                          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] flex items-center justify-center group-hover/specialty:scale-110 transition-transform">
                            <specialty.icon className="w-6 h-6 text-white" />
                          </div>
                          <span className="text-sm font-semibold text-gray-300 group-hover/specialty:text-white transition-colors">
                            {specialty.label}
                          </span>
                          {/* Hover glow */}
                          <div className="absolute inset-0 bg-gradient-to-r from-[#C5A05E]/20 to-transparent opacity-0 group-hover/specialty:opacity-100 blur-xl transition-opacity duration-500 rounded-2xl" />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom decorative line */}
                  <div className="flex items-center gap-3 pt-6">
                    <div className="h-px flex-1 bg-gradient-to-r from-[#C5A05E] to-transparent" />
                    <div className="w-2 h-2 bg-[#C5A05E] rounded-full animate-pulse" />
                    <div className="h-px flex-1 bg-gradient-to-l from-[#C5A05E] to-transparent" />
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};